import React, { useCallback, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import moment, { type Moment } from 'moment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { currentCompanyDateFormatSelector } from 'domain/companies';
import { CellEditorWrapper } from './StyledComponents';

const ISO_DATE = 'YYYY-MM-DD';

/**
 * Inline date editor for the vendor-level "Valid Until" column.
 * Past dates are disabled (the value cannot be earlier than today). Commits an ISO
 * `YYYY-MM-DD` string via `onValueChange` + `stopEditing`; bails (reverts) on a past/invalid date.
 */
const ValidUntilDateCellEditor: React.FC<CustomCellEditorProps<unknown, string | null>> = ({
  value,
  onValueChange,
  stopEditing,
}) => {
  const dateFormat = useSelector(currentCompanyDateFormatSelector);
  const [open, setOpen] = useState(true);

  // Push valid, non-past dates into AG Grid's editing value as they are typed or picked.
  // AG Grid persists whatever the editing value is when editing stops (Enter / blur); typing
  // only reaches the picker's `onAccept` on calendar selection, so without forwarding the
  // change here a manually keyed-in date is torn down before it ever commits and is silently lost.
  const handleChange = useCallback(
    (day: Moment | null) => {
      if (day && day.isValid() && !day.isBefore(moment(), 'day')) {
        onValueChange(day.format(ISO_DATE));
      }
    },
    [onValueChange],
  );

  // Finalize the edit when the user accepts a date (picks a calendar day or confirms the field).
  // Refuse empty / invalid / past dates — revert by stopping without a value change.
  const handleAccept = useCallback(
    (day: Moment | null) => {
      if (!day || !day.isValid() || day.isBefore(moment(), 'day')) {
        stopEditing(true);
        return;
      }
      onValueChange(day.format(ISO_DATE));
      stopEditing();
    },
    [onValueChange, stopEditing],
  );

  return (
    <CellEditorWrapper>
      <DesktopDatePicker
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        format={dateFormat}
        value={value ? moment(value) : null}
        onChange={handleChange}
        onAccept={handleAccept}
        disablePast
        minDate={moment().startOf('day')}
        slotProps={{
          popper: { className: 'ag-custom-component-popup' },
          textField: {
            fullWidth: true,
            autoFocus: true,
            onKeyDown: (e) => {
              if (e.key === 'Escape') stopEditing(true);
            },
            sx: { '& .MuiInputBase-root': { height: 40, backgroundColor: 'background.paper' } },
          },
        }}
      />
    </CellEditorWrapper>
  );
};

export default ValidUntilDateCellEditor;
